import React from 'react';
import {
  DocumentIcon,
  DocumentTextIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { FileType, resolveFileType } from '../../types/fileType';

/**
 * The file-type icon that opens a document preview.
 *
 * Extracted from DocsTable so the Invoice Options dialog offers the SAME control rather
 * than a second one that looks similar: a biller ticking backup paperwork onto an invoice
 * is doing the same job as a dispatcher checking a document on the pro, and the colour
 * that tells them "this is a PDF" should not be a different red in the two places.
 */
export function iconForFileType(t: FileType): {
  Icon: React.ComponentType<{ className?: string }>;
  className: string;
} {
  switch (t) {
    case FileType.Pdf:
      return { Icon: DocumentTextIcon, className: 'h-5 w-5 text-red-600 dark:text-red-400' };
    case FileType.Image:
      return { Icon: PhotoIcon, className: 'h-5 w-5 text-emerald-600 dark:text-emerald-400' };
    case FileType.Word:
      return { Icon: DocumentTextIcon, className: 'h-5 w-5 text-blue-600 dark:text-blue-400' };
    case FileType.Excel:
      return { Icon: DocumentTextIcon, className: 'h-5 w-5 text-green-700 dark:text-green-400' };
    default:
      return { Icon: DocumentIcon, className: 'h-5 w-5 text-muted' };
  }
}

export default function DocFileIconButton({
  filetype,
  onPreview,
  testId,
}: {
  filetype: string | null | undefined;
  onPreview: () => void;
  testId?: string;
}) {
  const { Icon, className } = iconForFileType(resolveFileType(filetype));
  return (
    <button
      type="button"
      onClick={(e) => {
        // Both hosts put something on the surrounding element — a row click on the Docs
        // tab, a tick-box label in the invoice dialog — and previewing must not also do
        // whichever that is.
        e.stopPropagation();
        e.preventDefault();
        onPreview();
      }}
      className="inline-flex items-center justify-center p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
      aria-label="Preview document"
      data-testid={testId}
    >
      <Icon className={className} />
    </button>
  );
}
